import React, { useState, useEffect, useRef } from 'react';
import { AthenaLogo } from './AthenaLogo';
import { CognitiveOnboarding } from './CognitiveOnboarding';

// ── Keyframes + scrollbar (injected once) ─────────────────────────────────────
let _kfDone = false;
function injectKF() {
  if (_kfDone || typeof document === 'undefined') return;
  _kfDone = true;
  const s = document.createElement('style');
  s.id = 'ft-v5-kf';
  s.textContent = `
    @keyframes ftFadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes ftFadeIn  { from{opacity:0} to{opacity:1} }
    @keyframes ftCardIn  { from{opacity:0;transform:translateY(20px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
    .ft-scroll::-webkit-scrollbar { display: none; }
  `;
  document.head.appendChild(s);
}

// ── Mobile + viewport width ───────────────────────────────────────────────────
function useViewport() {
  const get = () => ({
    isMobile: typeof window !== 'undefined' && window.innerWidth <= 768,
    vw: typeof window !== 'undefined' ? window.innerWidth : 375,
  });
  const [v, setV] = useState(get);
  useEffect(() => {
    const h = () => setV(get());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

// ── Card size calculator ──────────────────────────────────────────────────────
function getCardSize(isMobile, vw) {
  if (!isMobile) return { w: 185, h: 258, ring: 116, avatar: 66 };
  // 3 cards + 2 inner gaps (14px) + 2 outer pads (16px) = vw
  const w = Math.min(148, Math.max(108, Math.floor((vw - 60) / 3)));
  const h = Math.round(w * 1.72);   // ~portrait ratio matching reference
  const ring   = Math.round(w * 0.62);
  const avatar = Math.round(ring * 0.58);
  return { w, h, ring, avatar };
}

// ── Persistence ───────────────────────────────────────────────────────────────
const DEFAULT_PROFILES = [
  { id: 'vitor',   name: 'Vitor',   gender: 'male',   hue: 214 },
  { id: 'juliana', name: 'Juliana', gender: 'female', hue: 244 },
];
function loadProfiles() {
  try { const r = localStorage.getItem('athena_focus_profiles'); return r ? JSON.parse(r) : DEFAULT_PROFILES; }
  catch { return DEFAULT_PROFILES; }
}
function saveProfiles(ps) {
  try { localStorage.setItem('athena_focus_profiles', JSON.stringify(ps)); } catch {}
}

// ── Neural canvas ─────────────────────────────────────────────────────────────
function NeuralCanvas({ isMobile }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    let raf; let w = 0, h = 0, t = 0, alive = true;
    const resize = () => { w = cv.width = window.innerWidth; h = cv.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);

    const COUNT = isMobile ? 28 : 50;
    const PTS = Array.from({ length: COUNT }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.12, vy: (Math.random() - 0.5) * 0.12,
      r: Math.random() * 1.1 + 0.3, a: Math.random() * 0.14 + 0.04,
    }));

    const TRACES = isMobile ? [
      { p: [-0.08, 1.2, 0.06, 0.65, 0.24, 0.28, 0.58, -0.04], a: 0.06, lw: 0.9 },
      { p: [1.10, 0.92, 0.92, 0.52, 0.72, 0.20, 0.38, -0.04], a: 0.055, lw: 0.85 },
    ] : [
      { p: [-0.08, 1.25, 0.06, 0.68, 0.25, 0.28, 0.62, -0.04], a: 0.075, lw: 1.0 },
      { p: [-0.14, 1.15, 0.04, 0.75, 0.18, 0.38, 0.52, 0.10],  a: 0.045, lw: 0.65 },
      { p: [-0.05, 1.38, 0.10, 0.60, 0.32, 0.20, 0.75, -0.08], a: 0.035, lw: 0.45 },
      { p: [1.10,  0.95, 0.92, 0.55, 0.72, 0.22, 0.35, -0.05], a: 0.065, lw: 0.95 },
      { p: [1.16,  0.82, 0.95, 0.48, 0.78, 0.18, 0.48,  0.08], a: 0.040, lw: 0.60 },
      { p: [1.06,  1.05, 0.88, 0.62, 0.65, 0.30, 0.28,  0.12], a: 0.028, lw: 0.40 },
    ];
    const CONN = isMobile ? 65 : 88;

    const draw = () => {
      if (!alive) return;
      t += 0.002;
      ctx.clearRect(0, 0, w, h);
      TRACES.forEach(tr => {
        const pulse = 0.5 + 0.5 * Math.sin(t * 0.55 + tr.p[0] * 4);
        ctx.beginPath();
        ctx.moveTo(tr.p[0]*w, tr.p[1]*h);
        ctx.bezierCurveTo(tr.p[2]*w, tr.p[3]*h, tr.p[4]*w, tr.p[5]*h, tr.p[6]*w, tr.p[7]*h);
        ctx.strokeStyle = `rgba(56,189,248,${tr.a * pulse})`; ctx.lineWidth = tr.lw; ctx.stroke();
      });
      PTS.forEach(p => {
        p.x += p.vx; if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        p.y += p.vy; if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(56,189,248,${p.a})`; ctx.fill();
      });
      for (let i = 0; i < PTS.length; i++) for (let j = i+1; j < PTS.length; j++) {
        const dx = PTS[i].x-PTS[j].x, dy = PTS[i].y-PTS[j].y, d = Math.sqrt(dx*dx+dy*dy);
        if (d < CONN) {
          ctx.beginPath(); ctx.moveTo(PTS[i].x, PTS[i].y); ctx.lineTo(PTS[j].x, PTS[j].y);
          ctx.strokeStyle = `rgba(56,189,248,${0.026*(1-d/CONN)})`; ctx.lineWidth = 0.3; ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { alive = false; cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [isMobile]);

  return <canvas ref={ref} style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:0 }} />;
}

// ── Avatar icon ───────────────────────────────────────────────────────────────
function AvatarIcon({ gender = 'male', size = 64, bright = false }) {
  const a = bright ? 0.6 : 0.42;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <circle cx="50" cy="31" r="19" fill={`rgba(56,189,248,${a*0.38})`} stroke={`rgba(56,189,248,${a*1.1})`} strokeWidth="1.4"/>
      {gender === 'female' ? (
        <>
          <path d="M 22 100 C 22 72 34 62 50 62 C 66 62 78 72 78 100 Z"
            fill={`rgba(56,189,248,${a*0.35})`} stroke={`rgba(56,189,248,${a})`} strokeWidth="1.1"/>
          <path d="M 31 25 Q 38 14 50 13 Q 62 14 69 25"
            stroke={`rgba(56,189,248,${a*0.55})`} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
        </>
      ) : (
        <path d="M 13 100 C 13 70 28 59 50 59 C 72 59 87 70 87 100 Z"
          fill={`rgba(56,189,248,${a*0.35})`} stroke={`rgba(56,189,248,${a})`} strokeWidth="1.1"/>
      )}
    </svg>
  );
}

// ── Back button ───────────────────────────────────────────────────────────────
function BackButton({ onBack, isMobile }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onBack}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title="Voltar para Athena 2.0"
      aria-label="Voltar para Athena 2.0"
      style={{
        position: 'fixed',
        top: isMobile ? 16 : 20,
        left: isMobile ? 16 : 20,
        zIndex: 910,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 6,
        // Mobile: square icon-only; Desktop: pill with text
        width:  isMobile ? 40 : 'auto',
        height: isMobile ? 40 : 'auto',
        padding: isMobile ? 0 : '7px 14px',
        borderRadius: isMobile ? 10 : 10,
        background: hov ? 'rgba(56,189,248,0.1)' : 'rgba(10,20,50,0.6)',
        border: hov ? '1px solid rgba(56,189,248,0.4)' : '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(12px)',
        color: hov ? '#38bdf8' : 'rgba(148,163,184,0.7)',
        fontSize: 13, fontWeight: 400, letterSpacing: '0.03em',
        cursor: 'pointer', transition: 'all .22s ease',
        fontFamily: 'inherit',
      }}
    >
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M 9 2 L 3.5 6.5 L 9 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {!isMobile && 'Voltar'}
    </button>
  );
}

// ── Delete button ─────────────────────────────────────────────────────────────
function DeleteBtn({ name, onDelete, cardHovered }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={e => {
        e.stopPropagation();
        if (window.confirm(`Excluir o perfil "${name}"?`)) onDelete();
      }}
      title={`Excluir ${name}`}
      style={{
        position: 'absolute', top: 8, right: 8,
        width: 20, height: 20, borderRadius: '50%',
        background: hov ? 'rgba(239,68,68,0.18)' : 'rgba(0,0,0,0.28)',
        border: hov ? '1px solid rgba(239,68,68,0.45)' : '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', padding: 0,
        color: hov ? 'rgba(252,165,165,0.95)' : 'rgba(255,255,255,0.35)',
        fontSize: 12, lineHeight: 1,
        opacity: cardHovered || hov ? 1 : 0.12,
        transition: 'all .2s ease',
      }}
    >×</button>
  );
}

// ── Profile card ──────────────────────────────────────────────────────────────
function ProfileCard({ profile, onClick, onDelete, animDelay = 0, cardSize, isMobile }) {
  const [hov, setHov] = useState(false);
  const h = profile.hue ?? 214;
  const { w: cW, h: cH, ring: ringW, avatar: avatarS } = cardSize;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        flexShrink: 0,
        cursor: 'pointer', userSelect: 'none',
        width: cW,
        scrollSnapAlign: 'start',
        opacity: 0, animation: `ftCardIn .6s cubic-bezier(.22,1,.36,1) forwards`,
        animationDelay: `${animDelay}s`,
      }}
    >
      <div
        onClick={onClick}
        style={{
          width: '100%', height: cH,
          background: hov
            ? `linear-gradient(165deg, hsla(${h},62%,17%,0.95), rgba(5,10,26,0.98))`
            : 'linear-gradient(165deg, rgba(9,20,52,0.92), rgba(4,9,24,0.96))',
          border: hov
            ? '1px solid rgba(56,189,248,0.82)'
            : '1px solid rgba(56,189,248,0.22)',
          borderRadius: isMobile ? 16 : 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          boxShadow: hov
            ? `0 0 0 1px rgba(56,189,248,0.18), 0 20px 55px rgba(0,0,0,0.72), 0 0 45px hsla(${h},90%,55%,0.1)`
            : '0 6px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.03)',
          backdropFilter: 'blur(8px)',
          transition: 'all .28s ease',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 100%)',
          borderRadius: isMobile ? '16px 16px 0 0' : '20px 20px 0 0', pointerEvents: 'none',
        }} />
        <DeleteBtn name={profile.name} onDelete={onDelete} cardHovered={hov} />
        {/* Avatar ring */}
        <div style={{
          width: ringW, height: ringW, borderRadius: '50%',
          background: hov
            ? `radial-gradient(ellipse at 50% 35%, hsla(${h},65%,38%,0.22) 0%, rgba(7,14,40,0.85) 70%)`
            : 'radial-gradient(ellipse at 50% 35%, rgba(56,189,248,0.07) 0%, rgba(7,14,40,0.78) 70%)',
          border: hov ? '1.5px solid rgba(56,189,248,0.85)' : '1.5px solid rgba(56,189,248,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: hov
            ? '0 0 24px rgba(56,189,248,0.38), 0 0 52px rgba(56,189,248,0.12), inset 0 0 16px rgba(56,189,248,0.08)'
            : '0 0 10px rgba(56,189,248,0.1), inset 0 0 8px rgba(56,189,248,0.04)',
          transition: 'all .28s ease', zIndex: 1,
        }}>
          <AvatarIcon gender={profile.gender ?? 'male'} size={avatarS} bright={hov} />
        </div>
      </div>
      {/* Name + dash */}
      <div onClick={onClick} style={{
        marginTop: isMobile ? 10 : 18,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 5 : 8,
      }}>
        <span style={{
          fontSize: isMobile ? 13 : 15.5, fontWeight: 400, letterSpacing: '0.06em',
          color: hov ? '#eef6ff' : 'rgba(195,215,245,0.78)', transition: 'color .25s ease',
        }}>
          {profile.name}
        </span>
        <div style={{
          width: isMobile ? 22 : 28, height: 2, borderRadius: 2,
          background: hov ? 'rgba(56,189,248,0.88)' : 'rgba(56,189,248,0.32)',
          transition: 'background .25s ease',
        }} />
      </div>
    </div>
  );
}

// ── Add-profile card ──────────────────────────────────────────────────────────
function AddProfileCard({ onClick, animDelay = 0, cardSize, isMobile }) {
  const [hov, setHov] = useState(false);
  const { w: cW, h: cH } = cardSize;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        flexShrink: 0, cursor: 'pointer', userSelect: 'none',
        width: cW, scrollSnapAlign: 'start',
        opacity: 0, animation: `ftCardIn .6s cubic-bezier(.22,1,.36,1) forwards`,
        animationDelay: `${animDelay}s`,
      }}
    >
      <div style={{
        width: '100%', height: cH,
        background: hov ? 'rgba(56,189,248,0.03)' : 'transparent',
        border: hov ? '1px solid rgba(56,189,248,0.42)' : '1px dashed rgba(56,189,248,0.2)',
        borderRadius: isMobile ? 16 : 20,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        backdropFilter: hov ? 'blur(6px)' : 'none',
        boxShadow: hov ? '0 12px 40px rgba(0,0,0,0.4)' : 'none',
        transition: 'all .28s ease',
      }}>
        <div style={{
          width: isMobile ? 48 : 64, height: isMobile ? 48 : 64, borderRadius: '50%',
          border: hov ? '1.5px solid rgba(56,189,248,0.68)' : '1.5px dashed rgba(56,189,248,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: hov ? '0 0 18px rgba(56,189,248,0.2)' : 'none',
          transition: 'all .28s ease',
        }}>
          <span style={{
            fontSize: isMobile ? 22 : 28, fontWeight: 200, lineHeight: 1,
            color: hov ? 'rgba(56,189,248,0.9)' : 'rgba(56,189,248,0.3)',
            transition: 'color .25s ease',
          }}>+</span>
        </div>
      </div>
      <div style={{
        marginTop: isMobile ? 10 : 18,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 5 : 8,
      }}>
        <span style={{
          fontSize: isMobile ? 13 : 15.5, fontWeight: 400, letterSpacing: '0.06em',
          color: hov ? 'rgba(148,163,184,0.88)' : 'rgba(100,116,139,0.5)',
          transition: 'color .25s ease',
        }}>
          {isMobile ? 'Novo Perfil' : 'Criar Novo Perfil'}
        </span>
        <div style={{
          width: isMobile ? 22 : 28, height: 2, borderRadius: 2,
          background: hov ? 'rgba(56,189,248,0.58)' : 'rgba(56,189,248,0.15)',
          transition: 'background .25s ease',
        }} />
      </div>
    </div>
  );
}

// ── Creation panel ────────────────────────────────────────────────────────────
const HUES = [214, 244, 165, 338, 30, 188];

function CreatePanel({ onConfirm, onCancel, cardSize, isMobile }) {
  const [name,   setName]   = useState('');
  const [hue,    setHue]    = useState(214);
  const [gender, setGender] = useState('male');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const { w: cW, h: cH, ring: ringW, avatar: avatarS } = cardSize;

  const submit = () => {
    const n = name.trim(); if (!n) return;
    onConfirm({ id: Date.now().toString(36), name: n.slice(0,20), gender, hue });
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      flexShrink: 0, width: cW, scrollSnapAlign: 'start',
      opacity: 0, animation: 'ftCardIn .4s ease forwards',
    }}>
      <div style={{
        width: '100%', height: cH,
        background: 'linear-gradient(165deg, rgba(9,20,52,0.94), rgba(4,9,24,0.97))',
        border: '1px solid rgba(56,189,248,0.42)',
        borderRadius: isMobile ? 16 : 20,
        backdropFilter: 'blur(10px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: isMobile ? 8 : 10,
        padding: '0 12px', boxSizing: 'border-box',
        boxShadow: '0 8px 48px rgba(0,0,0,0.55)',
      }}>
        <div style={{
          width: isMobile ? Math.round(ringW * 0.78) : 68,
          height: isMobile ? Math.round(ringW * 0.78) : 68,
          borderRadius: '50%',
          background: `radial-gradient(ellipse at 50% 35%, hsla(${hue},65%,38%,0.22) 0%, rgba(7,14,40,0.82) 70%)`,
          border: '1.5px solid rgba(56,189,248,0.48)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AvatarIcon gender={gender} size={isMobile ? Math.round(avatarS * 0.8) : 40} bright />
        </div>

        <input
          ref={inputRef}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if(e.key==='Enter') submit(); if(e.key==='Escape') onCancel(); }}
          placeholder="Nome"
          maxLength={20}
          style={{
            width: '100%', padding: isMobile ? '6px 8px' : '7px 10px', textAlign: 'center',
            background: 'rgba(6,14,42,0.75)', border: '1px solid rgba(56,189,248,0.22)',
            borderRadius: 8, color: '#f0f6ff', fontSize: isMobile ? 12 : 13,
            outline: 'none', boxSizing: 'border-box', letterSpacing: '.04em',
          }}
        />

        <div style={{ display: 'flex', gap: 6 }}>
          {['male','female'].map(g => (
            <button key={g} onClick={() => setGender(g)} style={{
              padding: isMobile ? '3px 7px' : '4px 9px', fontSize: 10, borderRadius: 6, cursor: 'pointer',
              background: gender===g ? 'rgba(56,189,248,0.16)' : 'transparent',
              border: gender===g ? '1px solid rgba(56,189,248,0.4)' : '1px solid rgba(255,255,255,0.07)',
              color: gender===g ? '#38bdf8' : 'rgba(148,163,184,0.45)',
              transition: 'all .15s ease', fontFamily: 'inherit',
            }}>
              {g==='male' ? 'M' : 'F'}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {HUES.map(h => (
            <div key={h} onClick={() => setHue(h)} style={{
              width: 11, height: 11, borderRadius: '50%', cursor: 'pointer',
              background: `hsl(${h},65%,42%)`,
              border: hue===h ? '2px solid rgba(255,255,255,0.72)' : '2px solid transparent',
              transition: 'border .15s ease',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, width: '100%' }}>
          <button onClick={submit} style={{
            flex: 1, padding: isMobile ? '6px 0' : '7px 0', fontSize: 11, letterSpacing: '.04em',
            background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.32)',
            color: '#38bdf8', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
          }}>Criar</button>
          <button onClick={onCancel} style={{
            padding: isMobile ? '6px 8px' : '7px 10px', fontSize: 12,
            background: 'none', border: '1px solid rgba(255,255,255,0.07)',
            color: 'rgba(148,163,184,0.4)', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
          }}>✕</button>
        </div>
      </div>
      <div style={{ height: isMobile ? 34 : 46 }} />
    </div>
  );
}

// ── Scroll indicator (shown on mobile + desktop when >3 cards) ────────────────
function ScrollIndicator({ show }) {
  if (!show) return null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      marginTop: 18,
      opacity: 0, animation: 'ftFadeIn .5s ease 1.0s forwards',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15, color: 'rgba(56,189,248,0.42)', lineHeight: 1, userSelect: 'none' }}>‹</span>
        <div style={{ position: 'relative', width: 96, height: 1 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(56,189,248,0.18)', borderRadius: 1 }} />
          <div style={{ position: 'absolute', top: 0, left: 0, width: '35%', height: '100%', background: 'rgba(56,189,248,0.55)', borderRadius: 1 }} />
        </div>
        <span style={{ fontSize: 15, color: 'rgba(56,189,248,0.42)', lineHeight: 1, userSelect: 'none' }}>›</span>
      </div>
      <span style={{ fontSize: 10.5, color: 'rgba(148,163,184,0.38)', letterSpacing: '0.07em' }}>
        Deslize para ver mais perfis
      </span>
    </div>
  );
}

// ── Lock icon SVG ─────────────────────────────────────────────────────────────
function LockIcon() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" style={{ opacity: 0.4 }}>
      <rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="rgba(71,85,105,0.8)" strokeWidth="1"/>
      <path d="M 3 5.5 V 3.5 A 2.5 2.5 0 0 1 8 3.5 V 5.5" stroke="rgba(71,85,105,0.8)" strokeWidth="1" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function FocusTotalLanding({ onSelectProfile, onBack }) {
  const { isMobile, vw } = useViewport();
  const [profiles,         setProfiles]         = useState(loadProfiles);
  const [creating,         setCreating]         = useState(false);
  const [onboardingProfile, setOnboardingProfile] = useState(null);
  const [ready,            setReady]            = useState(false);

  useEffect(() => { injectKF(); requestAnimationFrame(() => setReady(true)); }, []);

  const handleDelete = (id) => {
    const updated = profiles.filter(p => p.id !== id);
    setProfiles(updated); saveProfiles(updated);
  };
  const handleCreate = (p) => {
    setCreating(false);
    setOnboardingProfile(p); // start cognitive onboarding before saving
  };

  const handleOnboardingComplete = (fullProfile) => {
    const updated = [...profiles, fullProfile];
    setProfiles(updated);
    saveProfiles(updated);
    setOnboardingProfile(null);
    onSelectProfile(fullProfile);
  };

  const cardSize  = getCardSize(isMobile, vw);
  const totalCards = profiles.length + 1;
  const useScroll  = isMobile ? totalCards >= 2 : totalCards > 3;
  const showScrollHint = isMobile || totalCards > 3;

  // Scroll container padding: on mobile, standard 16px; on desktop > 3 cards, 48px
  const scrollPadding = isMobile ? 16 : 48;

  // Responsive sizes
  const logoSize     = isMobile ? 86  : 130;
  const titleSize    = isMobile ? 'clamp(30px, 9.5vw, 44px)' : 'clamp(38px, 6.5vw, 68px)';
  const titleSpacing = isMobile ? '0.22em' : '0.4em';
  const subtitleMb   = isMobile ? 20 : 44;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900, overflow: 'hidden',
      background: 'radial-gradient(ellipse 110% 58% at 50% -3%, rgba(16,48,138,0.26) 0%, #030917 46%, #020508 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: isMobile ? 'flex-start' : 'center',
      paddingTop: isMobile ? 'max(52px, 7dvh)' : 0,
      fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
      opacity: ready ? 1 : 0, transition: 'opacity .5s ease',
    }}>

      <NeuralCanvas isMobile={isMobile} />

      {/* Vignette */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 65% 65% at 50% 50%, transparent 28%, rgba(2,5,14,0.62) 100%)',
      }} />

      {/* Top glow */}
      <div style={{
        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: isMobile ? 320 : 650, height: isMobile ? 200 : 300,
        zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(18,58,170,0.18) 0%, transparent 70%)',
      }} />

      {onBack && <BackButton onBack={onBack} isMobile={isMobile} />}

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', width: '100%' }}>

        {/* Official Athena logo */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          marginBottom: isMobile ? 10 : 20,
          opacity: 0, animation: 'ftFadeUp .65s ease .08s forwards',
        }}>
          <AthenaLogo size={logoSize} variant="vertical" />
        </div>

        {/* FOCO TOTAL */}
        <h1 style={{
          margin: 0, lineHeight: isMobile ? 1.05 : 1,
          fontSize: titleSize,
          fontWeight: 200, letterSpacing: titleSpacing,
          color: '#eef5ff', textTransform: 'uppercase',
          paddingLeft: titleSpacing,
          opacity: 0, animation: 'ftFadeUp .65s ease .22s forwards',
          whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'clip',
        }}>
          FOCO TOTAL
        </h1>

        {/* Subtitle with flanking dots + lines */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: isMobile ? 8 : 12,
          marginTop: isMobile ? 10 : 16,
          marginBottom: subtitleMb,
          opacity: 0, animation: 'ftFadeIn .55s ease .36s forwards',
          paddingLeft: isMobile ? 0 : 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: isMobile ? 22 : 44, height: 1, background: 'linear-gradient(90deg, transparent, rgba(56,189,248,0.38))' }} />
            <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(56,189,248,0.5)', flexShrink: 0 }} />
          </div>
          <span style={{
            fontSize: isMobile ? 10.5 : 12.5, letterSpacing: '0.06em', whiteSpace: 'nowrap',
            color: 'rgba(148,163,184,0.52)', fontWeight: 400,
          }}>
            Escolha um perfil para continuar
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(56,189,248,0.5)', flexShrink: 0 }} />
            <div style={{ width: isMobile ? 22 : 44, height: 1, background: 'linear-gradient(90deg, rgba(56,189,248,0.38), transparent)' }} />
          </div>
        </div>

        {/* ── Profile cards ────────────────────────────────────────────── */}
        <div
          className={useScroll ? 'ft-scroll' : undefined}
          style={{
            display: 'flex',
            gap: isMobile ? 14 : 22,
            justifyContent: useScroll ? 'flex-start' : 'center',
            alignItems: 'flex-start',
            overflowX: useScroll ? 'auto' : 'visible',
            scrollSnapType: useScroll ? 'x mandatory' : 'none',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: scrollPadding,
            paddingRight: scrollPadding,
            paddingTop: 8,
            paddingBottom: isMobile ? 4 : 14,
            width: '100%', maxWidth: '100vw', boxSizing: 'border-box',
            msOverflowStyle: 'none', scrollbarWidth: 'none',
          }}
        >
          {profiles.map((p, i) => (
            <ProfileCard
              key={p.id} profile={p}
              onClick={() => onSelectProfile(p)}
              onDelete={() => handleDelete(p.id)}
              animDelay={0.48 + i * 0.12}
              cardSize={cardSize}
              isMobile={isMobile}
            />
          ))}
          {creating ? (
            <CreatePanel
              onConfirm={handleCreate}
              onCancel={() => setCreating(false)}
              cardSize={cardSize}
              isMobile={isMobile}
            />
          ) : (
            <AddProfileCard
              onClick={() => setCreating(true)}
              animDelay={0.48 + profiles.length * 0.12}
              cardSize={cardSize}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Scroll indicator */}
        <ScrollIndicator show={showScrollHint} />

      </div>

      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: isMobile ? 16 : 18, zIndex: 2,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
        opacity: 0, animation: 'ftFadeIn .6s ease 1.1s forwards',
      }}>
        {isMobile && <LockIcon />}
        <div style={{
          fontSize: isMobile ? 9.5 : 10, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'rgba(71,85,105,0.42)',
        }}>
          {isMobile ? 'Athena Focus System v1' : 'Adaptive Cognitive Workspace'}
        </div>
      </div>

      {/* Cognitive onboarding overlay — shown after "Criar" in CreatePanel */}
      {onboardingProfile && (
        <CognitiveOnboarding
          profileBase={onboardingProfile}
          onComplete={handleOnboardingComplete}
          onBack={() => setOnboardingProfile(null)}
          isMobile={isMobile}
        />
      )}

    </div>
  );
}
